import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { registerUser } from "../../../data";
import AuthStore from "../../../stores/AuthStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Eye, EyeOff } from "lucide-react";
import { AuthLayout } from "../shared/layout";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";

export function RegisterScreen() {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { control, handleSubmit, formState: { errors } } = useForm();

  const { mutate: registerMutation, isLoading } = useMutation({
    mutationKey: "register",
    mutationFn: (data) => registerUser(data),
    onSuccess: (response) => {
      AuthStore.setUser(response.data.token);
      navigate("/dashboard");
      toast({
        title: t("auth.register.successTitle"),
        description: t("auth.register.successDescription"),
      });
    },
    onError: (error) => {
      toast({
        title: t("common.error"),
        description: error.response?.data?.message || t("auth.register.failed"),
        variant: "destructive",
      });
    }
  });

  const onSubmit = (data) => {
    registerMutation(data);
  };

  return (
    <AuthLayout
      title={t("auth.register.title")}
      description={t("auth.register.description")}
    >
      <div className="grid gap-6">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="name">{t("auth.name")}</Label>
              <Controller
                name="name"
                control={control}
                rules={{ required: t("auth.validation.nameRequired") }}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="name"
                    placeholder="John Doe"
                    type="text"
                    disabled={isLoading}
                  />
                )}
              />
              {errors.name && <p className="text-sm text-red-500">{errors.name.message}</p>}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Controller
                name="email"
                control={control}
                rules={{ required: t("auth.validation.emailRequired") }}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="email"
                    placeholder="name@example.com"
                    type="email"
                    autoCapitalize="none"
                    autoComplete="email"
                    autoCorrect="off"
                    disabled={isLoading}
                  />
                )}
              />
              {errors.email && <p className="text-sm text-red-500">{errors.email.message}</p>}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password">{t("auth.password")}</Label>
              <div className="relative">
                <Controller
                  name="password"
                  control={control}
                  rules={{ required: t("auth.validation.passwordRequired") }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="password"
                      type={showPassword ? "text" : "password"}
                      disabled={isLoading}
                    />
                  )}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
              {errors.password && <p className="text-sm text-red-500">{errors.password.message}</p>}
            </div>
            <div className="grid gap-2">
              <Controller
                name="ageConfirmed"
                control={control}
                rules={{ required: t("auth.validation.ageRequired") }}
                render={({ field }) => (
                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="ageConfirmed"
                      checked={!!field.value}
                      onCheckedChange={field.onChange}
                      disabled={isLoading}
                    />
                    <Label htmlFor="ageConfirmed" className="text-sm font-normal leading-snug">
                      {t("auth.register.ageConfirmation")}
                    </Label>
                  </div>
                )}
              />
              {errors.ageConfirmed && (
                <p className="text-sm text-red-500">{errors.ageConfirmed.message}</p>
              )}
              <p className="text-xs text-muted-foreground">
                {t("auth.register.agreePrefix")}{" "}
                <Link to="/terms" className="underline">{t("landing.footer.terms")}</Link>{" "}
                {t("auth.register.and")}{" "}
                <Link to="/privacy" className="underline">{t("landing.footer.privacy")}</Link>.
              </p>
            </div>
            <Button disabled={isLoading}>
              {isLoading && (
                <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              )}
              {t("auth.register.createAccount")}
            </Button>
          </div>
        </form>

        <p className="px-8 text-center text-sm text-muted-foreground">
          {t("auth.register.hasAccount")}{" "}
          <Link
            to="/login"
            className="underline underline-offset-4 hover:text-primary"
          >
            {t("auth.register.signIn")}
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}
